/**
 * Pure trip-day/itinerary mutations for the trip planner.
 *
 * Every function here is (days, args) => nextDays — no React, no fetches —
 * extracted from useTripState so the dense index/date arithmetic is unit-
 * testable and the guard behavior is uniform by construction (the five
 * activity ops used to hand-roll the clone/guard/writeback scaffold with
 * drifted guards; withDayActivities is now the single copy).
 *
 * Convention: return the INPUT array unchanged (`=== days`) for a no-op —
 * useTripState's mutateTripDays uses that identity check to skip the dirty-
 * version bump, so a guarded gesture can't trigger a byte-identical autosave.
 * Ops that also move the trip's date range return { days, startDate?, endDate? }.
 */
import { getLocalDateString, addDays } from './dateUtils';
import { getTimeBlock, rescheduleActivityToDate } from './activityUtilities';
import { buildDayLocationsPatch, getDayLocations } from './dayLocations';

// Block order for sorting: activities are grouped by block, then by
// scheduledTime/sortOrder within block.
const BLOCK_ORDER = { all_day: 0, morning: 1, afternoon: 2, evening: 3 };

/**
 * Canonical empty-day shape. Previously seven inline literals that had
 * already drifted (only one set variant_label/variant_icon).
 *
 * `coords` ({lat, lng}) rides along when the caller inherited the location
 * from an existing stop — a day added after "… → Annecy" starts in Annecy
 * WITH Annecy's coordinates, so map arcs/weather don't have to re-geocode
 * the name. Location + mirror fields go through buildDayLocationsPatch so
 * the day's shape matches editor-saved days.
 */
export function makeEmptyDay(date, { location = '', accommodation = null, coords = null } = {}) {
  return {
    date,
    notes: '',
    plan_variant: 'A',
    variant_label: null,
    variant_icon: null,
    is_active_variant: 1,
    activities: [],
    accommodation,
    ...(location
      ? buildDayLocationsPatch([{ name: location, lat: coords?.lat ?? null, lng: coords?.lng ?? null }])
      : { location: '' })
  };
}

/** Distinct trip dates, ascending. */
export function uniqueSortedDates(days) {
  return [...new Set(days.map(d => d.date))].sort();
}

/**
 * Uniform clone/guard/writeback for activity-level ops: clones the target
 * day's activities, hands them to fn, writes the result back immutably.
 * Returns the input array unchanged when the day index is invalid or fn
 * signals a no-op by returning null/undefined.
 */
function withDayActivities(days, dayIndex, fn) {
  const day = days[dayIndex];
  if (!day) return days;
  const activities = fn([...(day.activities || [])], day);
  if (!activities) return days;
  const newDays = [...days];
  newDays[dayIndex] = { ...day, activities };
  return newDays;
}

// Strips the DB row id (a copy must not claim the original's row) but KEEPS
// clientUid: copying a stay onto an adjacent day means the user is extending
// the SAME stay by a night, so it should share one span/identity on save.
function copyAccommodationWithoutId(accommodation) {
  if (!accommodation) return null;
  const { id, ...rest } = accommodation;
  return { ...rest };
}

/**
 * The day number a row is ("Day 3"): its date's place among the trip's
 * dates. A row index is not one — a date with a Plan B has two rows.
 */
export function dayNumberOf(days, index) {
  const date = days?.[index]?.date;
  if (!date) return index + 1;
  return [...new Set(days.map(d => d.date))].sort().indexOf(date) + 1;
}

// A row with no flag is its date's main plan (the column defaults to 1).
const isMainPlan = (d) => !!(d.is_active_variant ?? 1);

/** Generate the day list for a date range (empty days, trip-level location). */
export function generateTripDays(start, end, tripLocation) {
  if (!start || !end) return [];

  // Hard cap to protect against unbounded loops if invalid dates leak through.
  // 366 days covers the longest realistic trip; anything larger is bad input.
  const MAX_TRIP_DAYS = 366;

  const startDateObj = new Date(start + 'T12:00:00');
  const endDateObj = new Date(end + 'T12:00:00');

  if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) return [];
  if (endDateObj < startDateObj) return [];

  const spanDays = Math.floor((endDateObj.getTime() - startDateObj.getTime()) / 86400000) + 1;
  if (spanDays > MAX_TRIP_DAYS) {
    console.warn(`generateTripDays: span ${spanDays} exceeds cap ${MAX_TRIP_DAYS}, returning empty`);
    return [];
  }

  const days = [];
  for (let d = new Date(startDateObj); d <= endDateObj; d.setDate(d.getDate() + 1)) {
    days.push(makeEmptyDay(getLocalDateString(d), { location: tripLocation }));
  }
  return days;
}

/** Shallow-merge updates into one day. */
export function updateDay(days, dayIndex, updates) {
  if (!days[dayIndex]) return days;
  const newDays = [...days];
  newDays[dayIndex] = { ...newDays[dayIndex], ...updates };
  return newDays;
}

/** Add an alternative plan variant (B, C, …) for a date. */
/** The most plans one date may have (every "add a plan" control stops here). */
export const MAX_PLANS_PER_DATE = 5;

/** The letter a new plan on this date gets: the first one free (A, B, C…). */
export function nextVariantId(days, date) {
  const used = new Set((days || []).filter(d => d.date === date).map(d => d.plan_variant || 'A'));
  for (let i = 0; i < 26; i++) {
    const id = String.fromCharCode(65 + i);
    if (!used.has(id)) return id;
  }
  return null;
}

export function addVariantForDate(days, date, variantData, tripLocation) {
  const existingVariants = days.filter(d => d.date === date);
  if (existingVariants.length >= MAX_PLANS_PER_DATE) return days;
  const nextId = nextVariantId(days, date);
  if (!nextId) return days;

  // From the date's main plan: its places, and its stay — a stay is the
  // night's, on every plan of it (utils/accommodationSpread).
  const baseDay = existingVariants.find(isMainPlan) || existingVariants[0] || { date, location: tripLocation };
  const newVariantDay = {
    date,
    location: baseDay.location,
    latitude: baseDay.latitude ?? null,
    longitude: baseDay.longitude ?? null,
    locations: Array.isArray(baseDay.locations) ? [...baseDay.locations] : undefined,
    notes: '',
    plan_variant: nextId,
    variant_label: variantData.label || null,
    variant_icon: variantData.icon || null,
    // A backup unless it happens too (a split day)
    is_active_variant: variantData.happening ? 1 : 0,
    activities: [],
    accommodation: baseDay.accommodation || null
  };

  const lastVariantIndex = days.reduce((lastIdx, day, idx) =>
    day.date === date ? idx : lastIdx, -1);

  const newDays = [...days];
  newDays.splice(lastVariantIndex + 1, 0, newVariantDay);
  return newDays;
}

/**
 * Plans on a date (trip_days rows sharing it, letters A, B, …). Each is
 * HAPPENING (`is_active_variant` 1) or a BACKUP (0, a rainy-day plan): a
 * date always has at least one happening plan, and may have two (a guys' day
 * and a girls' day). Every server reader of what a day holds — home, legs,
 * the brief, reminders, "how was it?" — reads the happening ones only.
 */
const byLetter = (a, b) => (a.plan_variant || 'A').localeCompare(b.plan_variant || 'A');

// A date's happening plan when it has none left: its lowest letter.
function keepOneHappening(days, date) {
  const plans = days.filter(d => d.date === date);
  if (plans.length === 0 || plans.some(isMainPlan)) return days;
  const heir = [...plans].sort(byLetter)[0];
  return days.map(d => (d === heir ? { ...d, is_active_variant: 1 } : d));
}

/** Delete a plan — refuses a date's only one; its last happening plan hands on. */
export function deleteVariant(days, dayIndex) {
  const dayToDelete = days[dayIndex];
  if (!dayToDelete) return days;
  const variantsForDate = days.filter(d => d.date === dayToDelete.date);
  if (variantsForDate.length <= 1) return days;
  return keepOneHappening(days.filter((_, i) => i !== dayIndex), dayToDelete.date);
}

/** "Use this plan": it happens that day, the date's other plans become backups. */
export function chooseOnlyPlan(days, dayIndex) {
  const day = days[dayIndex];
  if (!day) return days;
  let changed = false;
  const out = days.map(d => {
    if (d.date !== day.date) return d;
    const want = d === day ? 1 : 0;
    if ((isMainPlan(d) ? 1 : 0) === want) return d;
    changed = true;
    return { ...d, is_active_variant: want };
  });
  return changed ? out : days;
}

/**
 * "This happens too" (on) / "Make it a backup" (off). Off is refused for
 * the date's last happening plan.
 */
export function setPlanHappening(days, dayIndex, happening) {
  const day = days[dayIndex];
  if (!day || isMainPlan(day) === !!happening) return days;
  if (!happening && !days.some(d => d !== day && d.date === day.date && isMainPlan(d))) return days;
  return days.map(d => (d === day ? { ...d, is_active_variant: happening ? 1 : 0 } : d));
}

/**
 * Whether a plan can move to another date: not a date's only plan (the date
 * would be left with none), and the date it goes to has room.
 */
export function canMovePlan(days, dayIndex, toDate) {
  const day = days?.[dayIndex];
  if (!day || !toDate || toDate === day.date) return false;
  const target = days.filter(d => d.date === toDate);
  return target.length > 0 && target.length < MAX_PLANS_PER_DATE && days.some(d => d !== day && d.date === day.date);
}

/**
 * Move a plan to another day (a rainy-day plan to the day it rains): it
 * lands there as a backup — "Use this plan" makes it the day's — with its
 * stops, keeping its letter when that date has it free, and that night's
 * stay (a stay is the night's). The day it left keeps a happening plan.
 */
/** The letter a plan takes on the date it moves to: its own if free there. */
export function movedPlanLetter(days, dayIndex, toDate) {
  const own = days?.[dayIndex]?.plan_variant || 'A';
  return days.some(d => d.date === toDate && (d.plan_variant || 'A') === own) ? nextVariantId(days, toDate) : own;
}

export function movePlanToDate(days, dayIndex, toDate) {
  if (!canMovePlan(days, dayIndex, toDate)) return days;
  const day = days[dayIndex];
  const target = days.filter(d => d.date === toDate);
  const plan = movedPlanLetter(days, dayIndex, toDate);
  const moved = {
    ...day,
    date: toDate,
    plan_variant: plan,
    is_active_variant: 0,
    accommodation: (target.find(d => d.accommodation) || {}).accommodation || null,
    activities: (day.activities || []).map(a => rescheduleActivityToDate(a, toDate))
  };
  const rest = days.filter((_, i) => i !== dayIndex);
  const at = rest.findIndex(d => d.date > toDate || (d.date === toDate && byLetter(d, moved) > 0));
  const out = at === -1 ? [...rest, moved] : [...rest.slice(0, at), moved, ...rest.slice(at)];
  return keepOneHappening(out, day.date);
}

/** Add an activity to a day, slotted at the end of its time block. */
export function addActivity(days, dayIndex, activity, time) {
  return withDayActivities(days, dayIndex, (activities, day) => {
    // Determine sortOrder: place at end of the target block
    const targetBlock = activity.timeBlock || (time ? null : 'morning');
    const blockActivities = activities.filter(a => getTimeBlock(a) === (targetBlock || getTimeBlock(activity)));
    const maxSort = blockActivities.reduce((max, a) => Math.max(max, a.sortOrder || 0), 0);

    activities.push({
      ...activity,
      scheduledTime: time,
      scheduledDate: day.date,
      sortOrder: maxSort + 1,
      id: activity.id || `activity_${Date.now()}`
    });

    // Sort: by block order first, then by scheduledTime (if set), then by sortOrder
    activities.sort((a, b) => {
      const blockA = BLOCK_ORDER[getTimeBlock(a)] ?? 1;
      const blockB = BLOCK_ORDER[getTimeBlock(b)] ?? 1;
      if (blockA !== blockB) return blockA - blockB;
      // Within same block: scheduled times first, then by sortOrder
      if (a.scheduledTime && b.scheduledTime) return a.scheduledTime.localeCompare(b.scheduledTime);
      if (a.scheduledTime) return -1;
      if (b.scheduledTime) return 1;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });

    return activities;
  });
}

/**
 * Add several activities to a day in ONE change (the map's "Select places"
 * bulk add): each slotted at the end of its block in the order given, so a
 * caller that ordered them (utils/bulkAddOrder) keeps that order. One
 * mutation means one save / one co-editing op and one Undo. Each keeps its
 * own `scheduledTime` (a shared time window: the same start + duration on
 * every one) or none (a part of the day: `timeBlock`). Empty list = no-op.
 */
export function addActivities(days, dayIndex, activities) {
  if (!Array.isArray(activities) || activities.length === 0 || !days[dayIndex]) return days;
  return activities.reduce((acc, activity) => addActivity(acc, dayIndex, activity, activity.scheduledTime || null), days);
}

/**
 * Remove several activities from a day by id in ONE change — the Undo of a
 * bulk add (addActivities). Ids not on the day are ignored; none found =
 * no-op returning the input.
 */
export function removeActivities(days, dayIndex, ids) {
  const drop = new Set((ids || []).filter(Boolean));
  if (drop.size === 0) return days;
  return withDayActivities(days, dayIndex, (activities) => {
    // Any of a stop's ids: a re-read in co-editing mode hands the stop back
    // with its minted id as `id`/`activityId` and no `clientActivityId`.
    const kept = activities.filter(a => !(a && [a.id, a.activityId, a.activity_id, a.clientActivityId].some(k => k && drop.has(k))));
    return kept.length === activities.length ? null : kept;
  });
}

/** Remove an activity by index. */
export function removeActivity(days, dayIndex, activityIndex) {
  return withDayActivities(days, dayIndex, (activities) => {
    if (activityIndex < 0 || activityIndex >= activities.length) return null;
    activities.splice(activityIndex, 1);
    return activities;
  });
}

/** Merge updates into the activity matching id. */
export function updateActivity(days, dayIndex, activityId, updates) {
  return withDayActivities(days, dayIndex, (activities) => {
    const idx = activities.findIndex(a => a.id === activityId);
    if (idx === -1) return null;
    activities[idx] = { ...activities[idx], ...updates };
    return activities;
  });
}

/**
 * Reorder an activity within a day (move from one index to another).
 *
 * `timeBlock` (the itinerary's Reorder mode): the stop is placed by its part
 * of the day, not its clock — it takes that part and loses its exact time,
 * in the same change as the move (one save, one Undo). Without it a timed
 * stop's clock would put it straight back where it was (Kurtis 2026-10-30:
 * exact times are set in the timeline, a drag says "afternoon").
 */
export function reorderActivity(days, dayIndex, fromIndex, toIndex, timeBlock = null) {
  return withDayActivities(days, dayIndex, (activities) => {
    if (fromIndex < 0 || fromIndex >= activities.length) return null;
    if (toIndex < 0 || toIndex >= activities.length) return null;
    let [moved] = activities.splice(fromIndex, 1);
    if (timeBlock) moved = { ...moved, timeBlock, scheduledTime: null };
    activities.splice(toIndex, 0, moved);
    // Update sortOrder to reflect new positions (immutable)
    for (let i = 0; i < activities.length; i++) {
      activities[i] = { ...activities[i], sortOrder: i };
    }
    return activities;
  });
}

/** Atomically move an activity from one day to another. */
export function moveActivityBetweenDays(days, fromDayIndex, activityIndex, toDayIndex, timeBlock) {
  // Guard: same-day moves should use updateActivity instead
  if (fromDayIndex === toDayIndex) return days;
  const fromDay = days[fromDayIndex];
  const toDay = days[toDayIndex];
  if (!fromDay || !toDay) return days;

  const fromActivities = [...(fromDay.activities || [])];
  if (activityIndex < 0 || activityIndex >= fromActivities.length) return days;

  const newDays = [...days];
  const [activity] = fromActivities.splice(activityIndex, 1);
  newDays[fromDayIndex] = { ...fromDay, activities: fromActivities };

  const toActivities = [...(toDay.activities || [])];
  // Dragging a multi-day item to another day moves its whole span, not just
  // its start (rescheduleActivityToDate shifts endDate by the same delta).
  const movedActivity = {
    ...rescheduleActivityToDate(activity, toDay.date),
    timeBlock,
    scheduledTime: null
  };

  // Place at end of target block
  const maxSort = toActivities.reduce((max, a) => Math.max(max, a.sortOrder || 0), 0);
  movedActivity.sortOrder = maxSort + 1;
  toActivities.push(movedActivity);

  newDays[toDayIndex] = { ...toDay, activities: toActivities };
  return newDays;
}

/** Shift every day (and the range) by n days. Returns { days, startDate, endDate } deltas. */
export function shiftAllDates(days, daysToShift, startDate, endDate) {
  return {
    // Activities move with their day — scheduledDate AND a multi-day endDate
    // (rescheduleActivityToDate). Without it the server re-stamped only the
    // start, and a 3-day tour moved two days later lost its end date.
    days: days.map(day => {
      const date = addDays(day.date, daysToShift);
      return { ...day, date, activities: (day.activities || []).map(act => rescheduleActivityToDate(act, date)) };
    }),
    startDate: startDate ? addDays(startDate, daysToShift) : startDate,
    endDate: endDate ? addDays(endDate, daysToShift) : endDate
  };
}

/** Prepend a day before the current first date. Returns { days, startDate }. */
export function addDayAtStart(days, { startDate, tripLocation }) {
  if (days.length === 0) {
    const newDate = startDate || getLocalDateString(new Date());
    return { days: [makeEmptyDay(newDate, { location: tripLocation || '' })], startDate };
  }

  const sortedDays = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const newDate = addDays(sortedDays[0].date, -1);

  // Do NOT inherit day 1's accommodation onto a day prepended BEFORE the
  // trip — the night before you check in is rarely the same hotel, and
  // pre-filling it created phantom stays (review issue #5). Adding a day at
  // the END still inherits, since that's extending the final stay.
  // The prepended day starts where the (old) first day STARTS — inherit its
  // first stop with coordinates.
  const firstStop = getDayLocations(sortedDays[0])[0] || null;
  return {
    days: [makeEmptyDay(newDate, {
      location: firstStop?.name || tripLocation || '',
      coords: firstStop
    }), ...days],
    startDate: startDate ? addDays(startDate, -1) : startDate
  };
}

/** Append a day after the current last date. Returns { days, endDate }. */
export function addDayAtEnd(days, { endDate, tripLocation }) {
  if (days.length === 0) {
    const newDate = endDate || getLocalDateString(new Date());
    return { days: [makeEmptyDay(newDate, { location: tripLocation || '' })], endDate };
  }

  const sortedDays = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const lastDay = sortedDays[sortedDays.length - 1];

  // A travel day ends somewhere else than it starts — the appended day
  // begins where the previous day ENDED (its last stop, WITH coordinates).
  const lastStops = getDayLocations(lastDay);
  const endStop = lastStops.length > 0 ? lastStops[lastStops.length - 1] : null;
  return {
    days: [...days, makeEmptyDay(addDays(lastDay.date, 1), {
      location: endStop?.name || tripLocation || '',
      coords: endStop,
      accommodation: copyAccommodationWithoutId(lastDay.accommodation)
    })],
    endDate: endDate ? addDays(endDate, 1) : endDate
  };
}

/**
 * Reorder whole days by date (drag a day card before/after another).
 * Returns { days, startDate, endDate } or null for a no-op.
 */
export function reorderDays(days, fromDate, toDate, position) {
  if (fromDate === toDate) return null;

  const sortedDates = uniqueSortedDates(days);
  const fromIndex = sortedDates.indexOf(fromDate);
  const toIndex = sortedDates.indexOf(toDate);
  if (fromIndex === -1 || toIndex === -1) return null;

  const newDateOrder = [...sortedDates];
  newDateOrder.splice(fromIndex, 1);
  let insertIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
  if (position === 'after') insertIndex += 1;
  newDateOrder.splice(insertIndex, 0, fromDate);

  const dateMapping = {};
  newDateOrder.forEach((oldDate, index) => {
    dateMapping[oldDate] = addDays(sortedDates[0], index);
  });

  const newDays = days.map(day => {
    const newDate = dateMapping[day.date];
    return {
      ...day,
      date: newDate,
      activities: (day.activities || []).map(act => rescheduleActivityToDate(act, newDate))
    };
  });
  newDays.sort((a, b) => a.date.localeCompare(b.date));

  const allDates = uniqueSortedDates(newDays);
  return { days: newDays, startDate: allDates[0], endDate: allDates[allDates.length - 1] };
}

/**
 * Insert a fresh day after a date, shifting later days when needed.
 * Returns { days, endDate } (endDate only present when it grew) or null.
 */
export function insertDayAfter(days, afterDate, { tripLocation, endDate }) {
  const sortedDates = uniqueSortedDates(days);
  const afterIndex = sortedDates.indexOf(afterDate);
  if (afterIndex === -1) return null;

  const newDateStr = addDays(afterDate, 1);

  const prevDay = days.find(d => d.date === afterDate);
  const nextDate = sortedDates[afterIndex + 1];
  const nextDay = nextDate ? days.find(d => d.date === nextDate) : null;

  let inheritedAccommodation = null;
  if (prevDay?.accommodation && nextDay?.accommodation) {
    if (prevDay.accommodation.name === nextDay.accommodation.name) {
      inheritedAccommodation = copyAccommodationWithoutId(prevDay.accommodation);
    }
  } else if (prevDay?.accommodation && !nextDay) {
    inheritedAccommodation = copyAccommodationWithoutId(prevDay.accommodation);
  }

  // Inherit where the previous day ENDED (last stop on travel days), WITH
  // its coordinates.
  const prevStops = getDayLocations(prevDay || {});
  const prevEndStop = prevStops.length > 0 ? prevStops[prevStops.length - 1] : null;
  const newDay = makeEmptyDay(newDateStr, {
    location: prevEndStop?.name || tripLocation || '',
    coords: prevEndStop,
    accommodation: inheritedAccommodation
  });
  const dateExists = days.some(d => d.date === newDateStr);

  let newDays;
  if (dateExists) {
    newDays = days.map(day => {
      if (day.date > afterDate) {
        const shiftedDate = addDays(day.date, 1);
        return {
          ...day, date: shiftedDate,
          activities: (day.activities || []).map(act => rescheduleActivityToDate(act, shiftedDate))
        };
      }
      return day;
    });

    const insertIdx = newDays.reduce((lastIdx, day, idx) =>
      day.date === afterDate ? idx + 1 : lastIdx, 0);
    newDays.splice(insertIdx, 0, newDay);
  } else {
    const insertIdx = days.reduce((lastIdx, day, idx) =>
      day.date === afterDate ? idx + 1 : lastIdx, 0);
    newDays = [...days];
    newDays.splice(insertIdx, 0, newDay);
  }

  const allDates = uniqueSortedDates(newDays);
  const lastDate = allDates[allDates.length - 1];
  return { days: newDays, ...(lastDate > endDate ? { endDate: lastDate } : {}) };
}

/**
 * Delete all variants of a date, closing the gap (later days shift back).
 * Returns { days, startDate, endDate } or null when it's the only date.
 */
export function deleteDayByDate(days, dateToDelete) {
  const sortedDates = uniqueSortedDates(days);
  if (sortedDates.length <= 1) return null;

  const newDays = days
    .filter(day => day.date !== dateToDelete)
    .map(day => {
      if (day.date > dateToDelete) {
        const shiftedDate = addDays(day.date, -1);
        return {
          ...day, date: shiftedDate,
          activities: (day.activities || []).map(act => rescheduleActivityToDate(act, shiftedDate))
        };
      }
      return day;
    });

  newDays.sort((a, b) => a.date.localeCompare(b.date));
  const remainingDates = uniqueSortedDates(newDays);

  return {
    days: newDays,
    startDate: remainingDates[0],
    endDate: remainingDates[remainingDates.length - 1]
  };
}

/**
 * Sync day locations when the trip-level location changes. Updates days that
 * have the old location OR none set; multi-location days (hand-built travel
 * stop lists) are deliberately skipped. Returns the input array for a no-op.
 */
export function syncDayLocations(days, prevLocation, nextLocation, isMultiLocationDay) {
  const shouldSync = (day) => !isMultiLocationDay(day) &&
    (day.location === prevLocation || !day.location);
  if (!days.some(shouldSync)) return days;
  return days.map(day =>
    shouldSync(day)
      ? { ...day, ...buildDayLocationsPatch([{ name: nextLocation }]) }
      : day
  );
}
